import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InvestorsFilter from './app-components/find-investors-modules/investorsFilter';
import DealsFilter from './app-components/deals-modules/dealsFilter';
import { LayoutDashboard, AlignStartVertical, SearchCheck, Bot, BriefcaseBusiness, ChartLine, FileSearch } from 'lucide-react';
import {
  buildStatisticsRoute,
  getStatisticsSectionFromPath,
  isStatisticsRoutePath,
  STATISTICS_SECTIONS,
} from './app-components/statistics-modules/config';

const Sidebar = ({
  handleFilterChange,
  handleDealsFilterChange,
  onInvestorsFilterHydrated,
  filteredCount,
  totalResults,
  totalDealsCount,
  isLoading
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const getSidebarPath = useCallback((pathname) => {
    let nextPath = pathname;

    if (i18n.language !== 'en') {
      nextPath = nextPath.replace(`/${i18n.language}`, '');
    }

    nextPath = nextPath.replace(/^\/+/, '');
    nextPath = nextPath.replace(/^app\//, '');
    nextPath = nextPath.replace(/\/+$/, '');

    return nextPath;
  }, [i18n.language]);
  const currentPath = getSidebarPath(location.pathname);

  // State for collapsing/expanding filters
  const [showInvestorsFilter, setShowInvestorsFilter] = useState(
    () => currentPath === 'find-investors'
  );
  const [showDealsFilter, setShowDealsFilter] = useState(
    () => currentPath === 'deals'
  );
  const [showStatisticsMenu, setShowStatisticsMenu] = useState(
    () => isStatisticsRoutePath(currentPath)
  );

  // State controls mobile sidebar visibility
  const [openMobile, setOpenMobile] = useState(false);

  // Active tab
  const [activeItem, setActiveItem] = useState(currentPath);

  useEffect(() => {
    const path = getSidebarPath(location.pathname);

    setActiveItem(path);

    // ✅ Open the "Find Investors" filter on initial load
    setShowInvestorsFilter(path === 'find-investors');
    setShowDealsFilter(path === 'deals');
    setShowStatisticsMenu(isStatisticsRoutePath(path));
  }, [getSidebarPath, location.pathname]);

  // Check whether the tab is active
  const isActive = (path) => (
    path === 'statistics'
      ? isStatisticsRoutePath(activeItem)
      : activeItem === path
  );

  const activeStatisticsSection = buildStatisticsRoute(
    getStatisticsSectionFromPath(activeItem)
  );

  const investorsFilterPanel = useMemo(() => (
    <InvestorsFilter
      onFilterChange={handleFilterChange}
      onHydrated={onInvestorsFilterHydrated}
      filteredCount={filteredCount}
      totalResults={totalResults}
      isLoading={isLoading}
    />
  ), [filteredCount, handleFilterChange, isLoading, onInvestorsFilterHydrated, totalResults]);

  const dealsFilterPanel = useMemo(() => (
    <DealsFilter
      totalResults={totalDealsCount}
      onFilterChange={handleDealsFilterChange}
    />
  ), [handleDealsFilterChange, totalDealsCount]);

  // Handle menu item click
  const handleItemClick = (e, path) => {
    if (isActive(path)) {
      e.preventDefault();
      if (path === 'find-investors') {
        setShowInvestorsFilter((prev) => !prev);
      }
      if (path === 'deals') {
        setShowDealsFilter((prev) => !prev);
      }
      if (path === 'statistics') {
        setShowStatisticsMenu((prev) => !prev);
      }
      return;
    }

    setActiveItem(path);

    if (path === 'find-investors') {
      setShowInvestorsFilter(true);
    } else {
      setShowInvestorsFilter(false);
    }

    if (path === 'deals') {
      setShowDealsFilter(true);
    } else {
      setShowDealsFilter(false);
    }

    if (path === 'statistics') {
      setShowStatisticsMenu(true);
    } else {
      setShowStatisticsMenu(false);
    }
  };

  // Component for a single item
  const SidebarItem = ({
    to,
    icon: Icon, // icon can now be either a component or a string (image path)
    text,
    isDisabled = false,
    badgeText = '',
    isRestricted = false,
    tooltipText = '',
    showArrow = false,
    arrowExpanded = false,
    linkTo = null,
  }) => {
    const languagePrefix = i18n.language === 'en' ? '' : `/${i18n.language}`;
    const active = isActive(to);
    const destination = linkTo || to;
    const baseClassName = `
      flex items-center w-full p-2 rounded-lg text-sm leading-tight transition-all outline-none
      ${isDisabled
        ? 'cursor-not-allowed opacity-55'
        : active
          ? 'bg-stone-100'
          : 'hover:bg-stone-200'
      }
    `;

    const content = (
      <>
        <div className="grid place-items-center mr-4">
          {typeof Icon === 'string' ? (
            <img className="h-5 w-5" src={Icon} alt={`${text} icon`} />
          ) : (
            <Icon className="h-5 w-5 text-gray-600" />
          )}
        </div>
        <span className="flex-1">{text}</span>
        {badgeText && (
          <span className="ml-2 inline-flex shrink-0 items-center rounded-full border border-stone-200 bg-stone-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-stone-500">
            {badgeText}
          </span>
        )}

        {showArrow && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 ml-auto transition-transform ${arrowExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        )}
      </>
    );

    return (
      <div className="relative group">
        {isDisabled ? (
          <div className={baseClassName} aria-disabled="true">
            {content}
          </div>
        ) : active ? (
          <button
            type="button"
            onClick={(e) => {
              handleItemClick(e, to);
              if (openMobile) setOpenMobile(false);
            }}
            className={baseClassName}
          >
            {content}
          </button>
        ) : (
          <Link
            to={`${languagePrefix}/app/${destination}`}
            onClick={(e) => {
              handleItemClick(e, to);
              if (openMobile) setOpenMobile(false);
            }}
            className={baseClassName}
            role="button"
            tabIndex={0}
          >
            {content}
          </Link>
        )}

        {/* Tooltip (if access is restricted) */}
        {isRestricted && tooltipText && (
          <div className="tooltip-content absolute top-1/2 -translate-y-1/2 left-full ml-2 px-3 py-2 bg-stone-700 text-xs text-white rounded-lg shadow-lg hidden group-hover:block z-10">
            {tooltipText}
          </div>
        )}
      </div>
    );
  };

  // Shared sidebar content
  const renderSidebarContent = () => (
    <nav className="flex flex-col gap-1 min-w-[240px] font-sans text-sm font-normal">
      {/* Dashboard */}
      <SidebarItem
        to="dashboard"
        icon={LayoutDashboard}
        text={t('app:sidebar.dashboard')}
      />

      {/* CRM */}
      <SidebarItem
        to="crm"
        icon={AlignStartVertical}
        text={t('app:sidebar.CRM')}
      // Skip access check for CRM
      />

      {/* Find Investors */}
      <SidebarItem
        to="find-investors"
        icon={SearchCheck}
        text={t('app:sidebar.findInv')}
        showArrow={true}
        arrowExpanded={showInvestorsFilter && isActive('find-investors')}
      />
      {isActive('find-investors') && (
        <div className={showInvestorsFilter ? 'block' : 'hidden'} aria-hidden={!showInvestorsFilter}>
          {investorsFilterPanel}
        </div>
      )}
      <SidebarItem
        to="ai-funding"
        icon={Bot}
        text={t('app:sidebar.aiFunding')}
      />
      {/* Deals */}
      <SidebarItem
        to="deals"
        icon={BriefcaseBusiness}
        text={t('app:sidebar.deals')}
        // Skip access check for Deals
        showArrow={true}
        arrowExpanded={showDealsFilter && isActive('deals')}
      />
      {isActive('deals') && (
        <div className={showDealsFilter ? 'block' : 'hidden'} aria-hidden={!showDealsFilter}>
          {dealsFilterPanel}
        </div>
      )}

      <SidebarItem
        to="statistics"
        icon={ChartLine}
        text={t('app:sidebar.stats')}
        showArrow={true}
        arrowExpanded={showStatisticsMenu && isActive('statistics')}
        linkTo={buildStatisticsRoute('overview')}
      />
      {isActive('statistics') && (
        <div
          className={showStatisticsMenu ? 'block' : 'hidden'}
          aria-hidden={!showStatisticsMenu}
        >
          <div className="ml-8 mt-1 border-l border-stone-200 pl-4">
            <div className="flex flex-col gap-1">
              {STATISTICS_SECTIONS.map((section) => {
                const sectionPath = buildStatisticsRoute(section.key);
                const isSectionActive = activeStatisticsSection === sectionPath;
                const languagePrefix = i18n.language === 'en' ? '' : `/${i18n.language}`;

                return (
                  <Link
                    key={section.key}
                    to={`${languagePrefix}/app/${sectionPath}`}
                    onClick={() => {
                      setActiveItem(sectionPath);
                      if (openMobile) setOpenMobile(false);
                    }}
                    className={[
                      'rounded-md px-3 py-1.5 text-sm transition-colors',
                      isSectionActive
                        ? 'bg-stone-100 font-medium text-stone-900'
                        : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900',
                    ].join(' ')}
                  >
                    {t(`app:statistics.sections.${section.key}`)}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 border-t border-stone-200 pt-4">
        <SidebarItem
          to="deck-review"
          icon={FileSearch}
          text={t('app:sidebar.deckReview')}
          isDisabled={true}
          badgeText={t('app:sidebar.comingSoon')}
        />
      </div>
    </nav>
  );

  return (
    <>
      {/* Hamburger button (mobile only) */}
      <div className="md:hidden p-2">
        <button
          onClick={() => setOpenMobile(true)}
          className="flex flex-col space-y-1 focus:outline-none"
        >
          {/* Hamburger bars */}
          <span className="block w-6 h-0.5 bg-stone-800"></span>
          <span className="block w-6 h-0.5 bg-stone-800"></span>
          <span className="block w-6 h-0.5 bg-stone-800"></span>
        </button>
      </div>

      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex md:flex-col bg-white h-full w-72 pr-8">
        {renderSidebarContent()}
      </aside>

      {/* Mobile menu (overlay) */}
      {openMobile && (
        <div className="fixed inset-0 z-50 flex">
          {/* Background dimmer, closes on click */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setOpenMobile(false)}
          />

          {/* Sidebar content on the left */}
          <aside className="relative w-72 bg-white p-4 shadow-xl">
            {/* "Close" button (X) */}
            <button
              onClick={() => setOpenMobile(false)}
              className="absolute top-4 right-4 focus:outline-none text-gray-800"
            >
              ✕
            </button>

            {/* Main sidebar content */}
            {renderSidebarContent()}
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;
